import { DateTime } from 'luxon'

export const SCHEDULE_TYPE_SERIES = 'series'
export const SCHEDULE_TYPE_RECURRING = 'recurring'

export const MIN_RECURRING_DATES = 2
export const MAX_RECURRING_DATES = 52

export const END_TYPE_DATE = 'date'
export const END_TYPE_COUNT = 'count'

// Luxon weekday numbers (1 = Monday ... 7 = Sunday), in calendar display order.
export const WEEKDAYS = [
  { value: 7, short: 'S', label: 'Sunday' },
  { value: 1, short: 'M', label: 'Monday' },
  { value: 2, short: 'T', label: 'Tuesday' },
  { value: 3, short: 'W', label: 'Wednesday' },
  { value: 4, short: 'T', label: 'Thursday' },
  { value: 5, short: 'F', label: 'Friday' },
  { value: 6, short: 'S', label: 'Saturday' },
]

const toDateTime = value => {
  if (!value) return null
  if (DateTime.isDateTime(value)) return value.isValid ? value : null
  const parsed = DateTime.fromJSDate(
    value instanceof Date ? value : new Date(value),
  )
  return parsed.isValid ? parsed : null
}

const atTimeOf = (day, time) =>
  day.set({
    hour: time.hour,
    minute: time.minute,
    second: 0,
    millisecond: 0,
  })

const sundayWeekStart = day =>
  day.minus({ days: day.weekday % 7 }).startOf('day')

export const defaultRecurrenceRule = (session, now = DateTime.local()) => {
  const startDate =
    toDateTime(session?.startDate)?.startOf('day') ||
    now.plus({ weeks: 1 }).startOf('day')
  const endOfYear = now.endOf('year').startOf('day')
  const endDate =
    endOfYear.diff(startDate, 'weeks').weeks >= 4
      ? endOfYear
      : startDate.plus({ months: 3 })

  return {
    startDate,
    startTime:
      toDateTime(session?.startTime) || startDate.set({ hour: 18, minute: 0 }),
    endTime:
      toDateTime(session?.endTime) || startDate.set({ hour: 19, minute: 0 }),
    intervalWeeks: 1,
    weekdays: [startDate.weekday],
    endType: END_TYPE_DATE,
    endDate,
    count: 10,
  }
}

/**
 * Expands a weekly recurrence rule into session-shaped rows
 * (`{ startDate, startTime, endTime }`) for the `sessions` field array.
 * Weeks start on Sunday to match the weekday picker.
 */
export const generateOccurrences = (
  {
    startDate,
    startTime,
    endTime,
    intervalWeeks = 1,
    weekdays = [],
    endType = END_TYPE_DATE,
    endDate,
    count,
  },
  max = MAX_RECURRING_DATES,
) => {
  const firstDay = toDateTime(startDate)?.startOf('day')
  const start = toDateTime(startTime)
  const end = toDateTime(endTime)
  const lastDay =
    endType === END_TYPE_DATE ? toDateTime(endDate)?.startOf('day') : null
  const interval = Math.max(1, Number(intervalWeeks) || 1)
  const requested =
    endType === END_TYPE_COUNT ? Math.max(0, Number(count) || 0) : Infinity
  // One past the cap, so we can tell the coach dates were cut off.
  const limit = Math.min(requested, max + 1)

  if (!firstDay || !start || !end || weekdays.length === 0) {
    return { occurrences: [], truncated: false }
  }
  if (endType === END_TYPE_DATE && (!lastDay || lastDay < firstDay)) {
    return { occurrences: [], truncated: false }
  }

  const anchorWeek = sundayWeekStart(firstDay)
  const isBeforeEnd = day => !lastDay || day <= lastDay
  const occurrences = []

  for (
    let day = firstDay;
    occurrences.length < limit && isBeforeEnd(day);
    day = day.plus({ days: 1 })
  ) {
    const weekIndex = Math.round(
      sundayWeekStart(day).diff(anchorWeek, 'weeks').weeks,
    )
    if (weekIndex % interval === 0 && weekdays.includes(day.weekday)) {
      occurrences.push({
        startDate: day,
        startTime: atTimeOf(day, start),
        endTime: atTimeOf(day, end),
      })
    }
  }

  return {
    occurrences: occurrences.slice(0, max),
    truncated: occurrences.length > max,
  }
}
